import {useContext, useMemo} from 'react';
import {doc, getFirestore} from 'firebase/firestore';
import {useDocumentData} from 'react-firebase-hooks/firestore';

import {getSectionScheduleDocumentRef} from '@cip/core/firebase/client';
import {
  computeSectionWeekCredit,
  getUserSectionAttendanceDocumentPath,
  getUserSectionAttendanceIdsDocumentPath,
  getUserSectionExercisesDocumentPath,
  getUserSectionRecordingAttendanceDocumentPath,
  getUserSectionRecordingAttendanceIdsDocumentPath,
  readSectionWeekCreditInput,
  simTeachPuzzleProgressDocPath,
  teachBackSummaryDocPath,
  type SectionWeekCredit,
  type SectionWeekCreditInput,
} from '@cip/core/section';
import {CourseContext} from 'contexts/CourseContext';
import {simulateUsedCatchUps} from 'course/staffViewAs/staffViewAs';

/** The documents the credit rule reads that a viewer may be refused. */
export type SectionWeekCreditSource =
  | 'sectionAttendance'
  | 'sectionAttendanceIds'
  | 'sectionRecordingAttendance'
  | 'sectionRecordingAttendanceIds'
  | 'teachBackProgress'
  | 'exerciseProgress'
  | 'teachBackSummary'
  | 'schedule';

/** A student's section credit, live from Firestore. */
export interface SectionWeekCreditState {
  /** The credit; computed from whatever could be read once loading ends. */
  credit: SectionWeekCredit;
  /** The input the credit was computed from. */
  input: SectionWeekCreditInput;
  /** True until every document has answered. */
  isLoading: boolean;
  /**
   * Documents the viewer could not read, which counted as empty: the
   * recording and exercise documents until their rules ship, and the
   * teach-back summaries for anyone but the student and admins.
   */
  unreadable: SectionWeekCreditSource[];
}

/**
 * Subscribes to everything the section credit rule reads for one student and
 * applies it, so a page shows exactly what the certificate check counts.
 *
 * @param args.uid - The student; undefined subscribes to nothing.
 * @param args.courseId - The course.
 * @param args.assumeExercisesDoneWhenUnreadable - When the viewer cannot
 *   read the student's teach-back summary or exercise record, count every
 *   set exercise as done. For an issued certificate, which the server
 *   already checked. Omitted or false counts none as done.
 * @param args.usedCatchUps - Adds, in memory only, every catch-up the
 *   course allows on recordings of `sectionId`, leaving `avoidWeekIndex`
 *   (zero-based, or `null`) untouched; see `simulateUsedCatchUps`. For staff
 *   viewing the course as a made-up student. Omitted adds nothing.
 * @return The credit, its input, loading state, and unreadable documents.
 * @throws Never.
 */
export function useSectionWeekCredit({
  uid,
  courseId,
  assumeExercisesDoneWhenUnreadable = false,
  usedCatchUps,
}: {
  uid: string | undefined;
  courseId: string;
  assumeExercisesDoneWhenUnreadable?: boolean;
  usedCatchUps?: {sectionId: string; avoidWeekIndex: number | null};
}): SectionWeekCreditState {
  const {superSectionIds, isLoading: isCourseLoading} =
    useContext(CourseContext);
  const refs = useMemo(() => {
    if (!uid) {
      return null;
    }
    const db = getFirestore();
    const userId = uid;
    return {
      sectionAttendance: doc(
        db,
        getUserSectionAttendanceDocumentPath({userId, courseId}),
      ),
      sectionAttendanceIds: doc(
        db,
        getUserSectionAttendanceIdsDocumentPath({userId, courseId}),
      ),
      sectionRecordingAttendance: doc(
        db,
        getUserSectionRecordingAttendanceDocumentPath({userId, courseId}),
      ),
      sectionRecordingAttendanceIds: doc(
        db,
        getUserSectionRecordingAttendanceIdsDocumentPath({userId, courseId}),
      ),
      teachBackProgress: doc(
        db,
        simTeachPuzzleProgressDocPath({uid: userId, courseId}),
      ),
      exerciseProgress: doc(
        db,
        getUserSectionExercisesDocumentPath({userId, courseId}),
      ),
      teachBackSummary: doc(db, teachBackSummaryDocPath({uid: userId, courseId})),
    };
  }, [uid, courseId]);
  const scheduleRef = useMemo(
    () => getSectionScheduleDocumentRef({courseId}),
    [courseId],
  );

  const [attendance, attendanceLoading, attendanceError] = useDocumentData(
    refs?.sectionAttendance,
  );
  const [attendanceIds, attendanceIdsLoading, attendanceIdsError] =
    useDocumentData(refs?.sectionAttendanceIds);
  const [recording, recordingLoading, recordingError] = useDocumentData(
    refs?.sectionRecordingAttendance,
  );
  const [recordingIds, recordingIdsLoading, recordingIdsError] =
    useDocumentData(refs?.sectionRecordingAttendanceIds);
  const [progress, progressLoading, progressError] = useDocumentData(
    refs?.teachBackProgress,
  );
  const [exercises, exercisesLoading, exercisesError] = useDocumentData(
    refs?.exerciseProgress,
  );
  const [summary, summaryLoading, summaryError] = useDocumentData(
    refs?.teachBackSummary,
  );
  const [schedule, scheduleLoading, scheduleError] =
    useDocumentData(scheduleRef);

  const usedCatchUpsSectionId = usedCatchUps?.sectionId;
  const usedCatchUpsAvoidWeekIndex = usedCatchUps?.avoidWeekIndex ?? null;

  const isLoading =
    isCourseLoading ||
    attendanceLoading ||
    attendanceIdsLoading ||
    recordingLoading ||
    recordingIdsLoading ||
    progressLoading ||
    exercisesLoading ||
    summaryLoading ||
    scheduleLoading;

  return useMemo(() => {
    const errors: Array<[SectionWeekCreditSource, Error | undefined]> = [
      ['sectionAttendance', attendanceError],
      ['sectionAttendanceIds', attendanceIdsError],
      ['sectionRecordingAttendance', recordingError],
      ['sectionRecordingAttendanceIds', recordingIdsError],
      ['teachBackProgress', progressError],
      ['exerciseProgress', exercisesError],
      ['teachBackSummary', summaryError],
      ['schedule', scheduleError],
    ];
    const unreadable = errors.flatMap(([source, error]) => {
      if (error === undefined) {
        return [];
      }
      console.debug('[useSectionWeekCredit] unreadable', source, error);
      return [source];
    });
    const readInput = readSectionWeekCreditInput({
      sectionAttendance: attendance,
      sectionAttendanceIds: attendanceIds,
      sectionRecordingAttendance: recording,
      sectionRecordingAttendanceIds: recordingIds,
      superSectionIds,
      schedule,
      teachBackProgress: progress,
      exerciseProgress: exercises,
      teachBackSummary: summary,
    });
    const assumedInput =
      assumeExercisesDoneWhenUnreadable &&
      (progressError !== undefined ||
        exercisesError !== undefined ||
        summaryError !== undefined)
        ? {...readInput, completedExerciseWeeks: readInput.exerciseWeeks}
        : readInput;
    const input =
      usedCatchUpsSectionId === undefined
        ? assumedInput
        : simulateUsedCatchUps({
            input: assumedInput,
            sectionId: usedCatchUpsSectionId,
            avoidWeekIndex: usedCatchUpsAvoidWeekIndex,
          });
    return {
      credit: computeSectionWeekCredit(input),
      input,
      isLoading,
      unreadable,
    };
  }, [
    attendance,
    attendanceIds,
    recording,
    recordingIds,
    progress,
    exercises,
    summary,
    schedule,
    superSectionIds,
    attendanceError,
    attendanceIdsError,
    recordingError,
    recordingIdsError,
    progressError,
    exercisesError,
    summaryError,
    scheduleError,
    assumeExercisesDoneWhenUnreadable,
    usedCatchUpsSectionId,
    usedCatchUpsAvoidWeekIndex,
    isLoading,
  ]);
}
